import { useState, useCallback, useRef } from 'react'
import {
  getAvailableCharacters,
  updateCharacter as updateCharacterApi
} from '../../../services/api/aiVideoGeneration'
import useTaskPoller from './useTaskPoller'

/**
 * Custom hook to manage character fetching and state for VideoAi
 * Follows the same pattern as useImageGeneration and useVideoGeneration
 */
export const useCharacters = ({ onCharactersLoaded, videoId } = {}) => {
  const [characters, setCharacters] = useState([])
  const [isFetchingCharacters, setIsFetchingCharacters] = useState(false)
  const hasFetchedRef = useRef(false)

  const { resolveTaskData } = useTaskPoller({ pollInterval: 2000 })

  const fetchCharacters = useCallback(
    async (force = false) => {
      // Skip if already fetched and not forcing
      if (hasFetchedRef.current && !force) {
        return characters
      }

      // Skip if already fetching
      if (isFetchingCharacters) {
        return characters
      }

      try {
        setIsFetchingCharacters(true)
        const response = await getAvailableCharacters({ video_id: videoId })

        const taskResult = await resolveTaskData(response, {
          getTaskId: data =>
            data?.['task-id'] ||
            data?.task_id ||
            data?.taskId ||
            ((data?.status === 'pending' || data?.status === 'processing') &&
              data?.id),
          getDirectOutput: data =>
            data && data.output !== undefined ? data.output : data,
          isDirectResponse: (data, output) => {
            const status =
              typeof data?.status === 'string' ? data.status.toLowerCase() : ''
            return (
              status === 'success' ||
              data?.success === true ||
              Array.isArray(output)
            )
          }
        })

        const charArray = Array.isArray(taskResult?.output)
          ? taskResult.output
          : Array.isArray(taskResult?.output?.items)
            ? taskResult.output.items
            : []
        if (!Array.isArray(charArray)) {
          throw new Error('Invalid response format: output is not an array')
        }

        const formattedCharacters = charArray
          .filter(Boolean)
          .map((item, index) => ({
            ...item,
            id: index + 1
          }))

        setCharacters(formattedCharacters)
        hasFetchedRef.current = true

        if (onCharactersLoaded) {
          onCharactersLoaded(formattedCharacters)
        }

        return formattedCharacters
      } catch (error) {
        console.error('Error fetching characters:', error)
        return []
      } finally {
        setIsFetchingCharacters(false)
      }
    },
    [
      isFetchingCharacters,
      onCharactersLoaded,
      videoId,
      resolveTaskData,
      characters
    ]
  )

  const editCharacter = useCallback(async (payload) => {
    try {
      const response = await updateCharacterApi(payload)
      return await resolveTaskData(response, {
        getDirectOutput: data =>
          data && data.output !== undefined ? data.output : data
      })
    } catch (error) {
      throw error
    }
  }, [resolveTaskData])

  const resetCharacters = useCallback(() => {
    setCharacters([])
    hasFetchedRef.current = false
  }, [])

  return {
    characters,
    setCharacters,
    isFetchingCharacters,
    fetchCharacters,
    editCharacter,
    resetCharacters,
    hasFetched: hasFetchedRef.current
  }
}

export default useCharacters
